import type { User } from '@/payload-types'

/**
 * The subset of a Users document that is allowed to cross into the client tree.
 *
 * Every prop a Server Component hands to a `'use client'` component is serialised into the page's
 * public RSC payload — all of it, not just what gets rendered. The app shell used to pass the whole
 * `User` document straight into `UserProvider`, so every authenticated page carried the visitor's
 * `_verified`, their depth-1 `profile` (phone, address, zipCode, dob) and — the reason this exists —
 * their `magicTokens`: live passwordless-login tokens, verbatim in the HTML.
 * `Users.magicTokens` and `Users.activeSessions` do have a field-level read gate, but `getUser()`
 * reads through the Local API, which defaults to `overrideAccess: true` and skips it (issue 2201).
 *
 * Keeping the client context typed as this DTO rather than `User` means a field added to the Users
 * collection later cannot silently start crossing the boundary again: it has to be added here on
 * purpose.
 */
export interface PublicUser {
  id: string
  name?: string | null
  email?: string | null
  /**
   * The visitor's own roles. Client components gate their admin/moderator UI on these
   * (`AdminRoute`, `AdminDashboard`, `NavDrawer`), and they are the user's own — the server
   * re-checks every privileged action regardless of what the client believes.
   */
  roles?: User['roles']
  /**
   * The profile's **id**, never the populated document — callers only test whether a profile
   * exists (to decide whether onboarding is finished). Passing the populated `Profile` here would
   * put the visitor's phone and home address back into the payload.
   *
   * Pages that genuinely render profile data (`/mijn/profiel`, `/profiel/[username]`) already
   * receive the `Profile` document as its own prop, filtered by the visibility rules — they do
   * not need it hanging off the user.
   */
  profile?: string | null
  /**
   * Whether media consent has been given. The `/app` dashboard decides on this whether to show
   * the consent request, and it is the visitor's own flag — but it is the only reason this field
   * is here, so weigh that before adding the next one.
   */
  mediaConsent?: boolean | null
}

/** Narrow a Users document (populated or not) to the shape the client is allowed to see. */
export function toPublicUser(user: User): PublicUser
export function toPublicUser(user: User | null | undefined): PublicUser | null
export function toPublicUser(user: User | null | undefined): PublicUser | null {
  if (!user) return null

  const profile = user.profile
  return {
    id: user.id,
    name: user.name ?? null,
    email: user.email ?? null,
    roles: user.roles ?? null,
    profile: profile && typeof profile === 'object' ? profile.id : (profile ?? null),
    mediaConsent: user.mediaConsent ?? null,
  }
}

/**
 * Narrow an untyped user payload — e.g. the JSON body of `/api/users/login` or `/api/users/me` —
 * to {@link PublicUser}, so client state holds the same shape no matter where it came from.
 */
export function toPublicUserFromJson(value: unknown): PublicUser | null {
  if (!value || typeof value !== 'object') return null
  const user = value as Record<string, unknown>
  if (typeof user.id !== 'string') return null

  const profile = user.profile
  return {
    id: user.id,
    name: typeof user.name === 'string' ? user.name : null,
    email: typeof user.email === 'string' ? user.email : null,
    roles: Array.isArray(user.roles) ? (user.roles as NonNullable<User['roles']>) : null,
    mediaConsent: typeof user.mediaConsent === 'boolean' ? user.mediaConsent : null,
    profile:
      profile && typeof profile === 'object'
        ? ((profile as { id?: unknown }).id as string | undefined) ?? null
        : typeof profile === 'string'
          ? profile
          : null,
  }
}
